import { unparse } from 'papaparse';

import type { FinanceForecastRow } from './ForecastTable';
import {
  formatCompareDelta,
  formatFinancePeriodLabel,
  formatFinanceValue,
  type FinanceMetricFormat,
} from './formatters';
import {
  buildFinanceAdditiveTotals,
  resolveFinanceRowTotal,
} from './financeTotals';

export type FinanceCsvInput = {
  periods: string[];
  rows: FinanceForecastRow[];
  granularity?: string;
  dateRangeLabel: string;
  compareActive?: boolean;
  generatedAt?: Date;
};

export function buildFinanceCsvFilename(
  dateRangeLabel: string,
  generatedAt = new Date(),
): string {
  const stamp = generatedAt.toISOString().slice(0, 10);
  const rangeSlug = dateRangeLabel
    .replace(/[–—]/g, '-')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return `kipify-finance-pnl-${rangeSlug || 'report'}-${stamp}.csv`;
}

/** Flat table for CSV: every period column included (no chunking). */
export function buildFinanceCsvTable(input: FinanceCsvInput): string[][] {
  const periodHeaders = input.periods.map((period) =>
    formatFinancePeriodLabel(period, input.granularity),
  );
  const includeCompare = Boolean(input.compareActive);

  const header = includeCompare
    ? [
        'Metric',
        ...periodHeaders.flatMap((label) => [label, `${label} Δ`]),
        'Total',
        'Total Δ',
      ]
    : ['Metric', ...periodHeaders, 'Total'];

  const additiveTotals = buildFinanceAdditiveTotals(input.rows, 'values');
  const compareAdditiveTotals = includeCompare
    ? buildFinanceAdditiveTotals(input.rows, 'compareValues')
    : null;

  const body: string[][] = [];
  for (const row of input.rows) {
    if (row.emphasis === 'spacer') continue;

    if (row.emphasis === 'section') {
      const emptyCols = includeCompare
        ? input.periods.length * 2 + 2
        : input.periods.length + 1;
      body.push([row.label, ...Array.from({ length: emptyCols }, () => '')]);
      continue;
    }

    const cells: string[] = [row.label];
    for (let i = 0; i < input.periods.length; i += 1) {
      const value = row.values[i] ?? 0;
      cells.push(formatFinanceValue(value, row.format as FinanceMetricFormat));
      if (includeCompare) {
        const compareValue = row.compareValues?.[i] ?? null;
        const delta =
          compareValue !== null
            ? formatCompareDelta(
                value,
                compareValue,
                row.format as FinanceMetricFormat,
              )
            : null;
        cells.push(delta ?? '');
      }
    }

    const totalValue = resolveFinanceRowTotal(row, additiveTotals);
    cells.push(
      totalValue === null
        ? ''
        : formatFinanceValue(totalValue, row.format as FinanceMetricFormat),
    );
    if (includeCompare) {
      const compareTotal = compareAdditiveTotals
        ? resolveFinanceRowTotal(row, compareAdditiveTotals)
        : null;
      const totalDelta =
        totalValue !== null && compareTotal !== null
          ? formatCompareDelta(
              totalValue,
              compareTotal,
              row.format as FinanceMetricFormat,
            )
          : null;
      cells.push(totalDelta ?? '');
    }
    body.push(cells);
  }

  return [header, ...body];
}

export function downloadFinanceCsv(input: FinanceCsvInput): void {
  if (!input.periods.length || !input.rows.length) {
    throw new Error('Nessun dato finance da esportare.');
  }

  const table = buildFinanceCsvTable(input);
  const csv = unparse(table);
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildFinanceCsvFilename(
    input.dateRangeLabel,
    input.generatedAt,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
